import React, { useEffect, useMemo, useState } from "react";
import { X, CalendarDays, Clock, Lock } from "lucide-react";
import Toast from "./Toast";
import "../../Product/Scheduling/scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";

function toIsoLocalInput(dt) {
  // expects Date
  const pad = (n) => String(n).padStart(2, "0");
  const y = dt.getFullYear();
  const m = pad(dt.getMonth() + 1);
  const d = pad(dt.getDate());
  const hh = pad(dt.getHours());
  const mm = pad(dt.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function parseLocalInputToTimestamptz(val) {
  // val: "YYYY-MM-DDTHH:mm"
  // Convert to ISO string with timezone via Date constructor
  const d = new Date(val);
  return d.toISOString();
}

function getLocalTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export default function BookingModal({
  open,
  onClose,
  mode = "create", // create | edit
  spaces = [],
  booking, // when edit
  defaultSpaceId,
  defaultStart,
  defaultEnd,
  onSaved,
}) {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });
  const [err, setErr] = useState("");

  const initial = useMemo(() => {
    const now = new Date();
    const start = defaultStart || new Date(now.getTime() + 30 * 60000);
    const end = defaultEnd || new Date(start.getTime() + 60 * 60000);
    return {
      space_id: defaultSpaceId || "",
      title: "",
      notes: "",
      private_mode: false,
      start_local: toIsoLocalInput(start),
      end_local: toIsoLocalInput(end),
      override_used: false,
      override_reason: "",
      recurring: false,
      recurrence_tz: getLocalTz(),
      byweekday: [], // e.g. [1,5]
      until_date: "", // YYYY-MM-DD
    };
  }, [defaultSpaceId, defaultStart, defaultEnd]);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (mode === "edit" && booking) {
      setForm({
        space_id: booking.space_id,
        title: booking.private_mode ? "" : booking.title || "",
        notes: booking.notes || "",
        private_mode: !!booking.private_mode,
        start_local: toIsoLocalInput(new Date(booking.start_at)),
        end_local: toIsoLocalInput(new Date(booking.end_at)),
        override_used: !!booking.override_used,
        override_reason: booking.override_reason || "",
        recurring: false,
        recurrence_tz: getLocalTz(),
        byweekday: [],
        until_date: "",
      });
    } else {
      setForm(initial);
    }
  }, [open, mode, booking, initial]);

  if (!open) return null;

  const selectedSpace = spaces.find((s) => s.id === form.space_id);

  const validateClient = () => {
    if (!companyId) return "Company not loaded";
    if (!form.space_id) return "Please select a space";
    if (!form.private_mode && (!form.title || form.title.trim().length < 2))
      return "Title is required (min 2 characters)";
    if (!form.start_local || !form.end_local)
      return "Start and end time are required";
    if (form.recurring) {
      if (!form.byweekday || form.byweekday.length === 0)
        return "Select at least one weekday for recurrence";
      if (!form.until_date) return "Select an 'until' date for recurrence";
      if (!form.recurrence_tz || form.recurrence_tz.trim().length < 3)
        return "Timezone is required for recurrence";
    }

    const start = new Date(form.start_local);
    const end = new Date(form.end_local);

    if (!(end > start)) return "End time must be after start time";
    if (start < new Date()) return "Cannot book in the past";

    // basic duration hints (server is source of truth)
    if (selectedSpace) {
      const mins = Math.floor((end - start) / 60000);
      if (mins < selectedSpace.min_duration_min) {
        return `Duration must be at least ${selectedSpace.min_duration_min} minutes`;
      }
      if (mins > selectedSpace.max_duration_min) {
        return `Duration cannot exceed ${selectedSpace.max_duration_min} minutes`;
      }
    }

    if (
      form.override_used &&
      (!form.override_reason || form.override_reason.trim().length < 10)
    ) {
      return "Override reason must be at least 10 characters";
    }

    return "";
  };

  async function save() {
    const v = validateClient();
    if (v) {
      setErr(v);
      return;
    }
    setErr("");
    setBusy(true);

    try {
      if (mode === "create") {
        if (form.recurring) {
          const { data, error } = await supabase.rpc(
            "rpc_create_recurring_space_booking",
            {
              p_company_id: companyId,
              p_space_id: form.space_id,
              p_title: form.title?.trim() || null,
              p_notes: form.notes?.trim() || null,
              p_private_mode: !!form.private_mode,
              p_start_at: parseLocalInputToTimestamptz(form.start_local),
              p_end_at: parseLocalInputToTimestamptz(form.end_local),
              p_recurrence_tz: form.recurrence_tz?.trim() || "UTC",
              p_byweekday: form.byweekday,
              p_until_date: form.until_date, // YYYY-MM-DD
            }
          );

          if (error) throw error;

          setToast({
            open: true,
            type: "success",
            message: "Recurring series created",
          });
          onSaved?.({ series_id: data });
          onClose?.();
        } else {
          const { data, error } = await supabase.rpc(
            "rpc_create_space_booking",
            {
              p_company_id: companyId,
              p_space_id: form.space_id,
              p_title: form.title?.trim() || null,
              p_notes: form.notes?.trim() || null,
              p_private_mode: !!form.private_mode,
              p_start_at: parseLocalInputToTimestamptz(form.start_local),
              p_end_at: parseLocalInputToTimestamptz(form.end_local),
            }
          );

          if (error) throw error;

          setToast({ open: true, type: "success", message: "Booking created" });
          onSaved?.(data);
          onClose?.();
        }
      }
    } catch (e) {
      setErr(e?.message || "Failed to save booking");
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Failed to save booking",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sched-modal-backdrop" onMouseDown={onClose} />
      <div
        className="sched-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Booking"
      >
        <div className="sched-modal-head">
          <div>
            <h3 className="sched-h3">
              {mode === "create" ? "New Booking" : "Edit Booking"}
            </h3>
            <p className="sched-muted">
              Conflict prevention is enforced server-side. If it overlaps, it
              will be rejected.
            </p>
          </div>
          <button
            className="sched-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="sched-form">
          <label className="sched-label">Space</label>
          <select
            value={form.space_id}
            onChange={(e) =>
              setForm((p) => ({ ...p, space_id: e.target.value }))
            }
            disabled={busy || mode === "edit"} // edit doesn't allow changing space in this MVP (enterprise-safe)
          >
            <option value="">Select space</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id} disabled={s.status !== "active"}>
                {s.name} {s.status !== "active" ? "(Inactive)" : ""}
              </option>
            ))}
          </select>

          <div className="sched-grid-2">
            <div>
              <label className="sched-label">
                <CalendarDays size={16} /> Start
              </label>
              <input
                type="datetime-local"
                value={form.start_local}
                onChange={(e) =>
                  setForm((p) => ({ ...p, start_local: e.target.value }))
                }
                disabled={busy}
              />
            </div>
            <div>
              <label className="sched-label">
                <Clock size={16} /> End
              </label>
              <input
                type="datetime-local"
                value={form.end_local}
                onChange={(e) =>
                  setForm((p) => ({ ...p, end_local: e.target.value }))
                }
                disabled={busy}
              />
            </div>
          </div>

          <label className="sched-label">Title / Purpose</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder={
              form.private_mode
                ? "Hidden (Private booking)"
                : "E.g. Weekly ops meeting"
            }
            disabled={busy || form.private_mode}
          />

          <div className="sched-divider" />

          <div className="sched-row">
            <label className="sched-check">
              <input
                type="checkbox"
                checked={form.recurring}
                onChange={(e) => {
                  const on = e.target.checked;
                  setForm((p) => ({
                    ...p,
                    recurring: on,
                    // sensible defaults
                    byweekday: on
                      ? p.byweekday.length
                        ? p.byweekday
                        : [1]
                      : [],
                    until_date: on ? p.until_date || "" : "",
                    recurrence_tz: p.recurrence_tz || getLocalTz(),
                  }));
                }}
                disabled={busy || mode === "edit"} // keep series creation only in create mode
              />
              <span>Recurring booking (weekly)</span>
            </label>
          </div>

          {form.recurring ? (
            <>
              <div className="sched-muted">
                Industry standard: recurring bookings are created as a{" "}
                <b>series</b> with individual <b>instances</b>. If any instance
                conflicts, the entire series is rejected.
              </div>

              <label className="sched-label">Repeat on</label>
              <div className="sched-weekdays">
                {[
                  { k: 1, label: "Mon" },
                  { k: 2, label: "Tue" },
                  { k: 3, label: "Wed" },
                  { k: 4, label: "Thu" },
                  { k: 5, label: "Fri" },
                  { k: 6, label: "Sat" },
                  { k: 0, label: "Sun" },
                ].map((d) => {
                  const on = form.byweekday.includes(d.k);
                  return (
                    <button
                      key={d.k}
                      type="button"
                      className={`sched-daybtn ${on ? "on" : ""}`}
                      onClick={() =>
                        setForm((p) => {
                          const set = new Set(p.byweekday);
                          if (set.has(d.k)) set.delete(d.k);
                          else set.add(d.k);
                          return {
                            ...p,
                            byweekday: Array.from(set).sort((a, b) => a - b),
                          };
                        })
                      }
                      disabled={busy}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>

              <div className="sched-grid-2">
                <div>
                  <label className="sched-label">Repeat until (date)</label>
                  <input
                    type="date"
                    value={form.until_date}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, until_date: e.target.value }))
                    }
                    disabled={busy}
                  />
                </div>
                <div>
                  <label className="sched-label">Timezone</label>
                  <input
                    value={form.recurrence_tz}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, recurrence_tz: e.target.value }))
                    }
                    disabled={busy}
                    placeholder="e.g. Africa/Lagos"
                  />
                </div>
              </div>
            </>
          ) : null}

          <label className="sched-label">Notes (optional)</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            placeholder="Extra details..."
            disabled={busy}
            rows={3}
          />

          <div className="sched-row">
            <label className="sched-check">
              <input
                type="checkbox"
                checked={form.private_mode}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    private_mode: e.target.checked,
                    title: e.target.checked ? "" : p.title,
                  }))
                }
                disabled={busy}
              />
              <span>
                <Lock size={16} /> Private booking (show “Busy” to others)
              </span>
            </label>
          </div>

          {/* Admin override switch (UI shows it; server enforces reason and admin role) */}
          <div className="sched-row">
            <label className="sched-check">
              <input
                type="checkbox"
                checked={form.override_used}
                onChange={(e) =>
                  setForm((p) => ({ ...p, override_used: e.target.checked }))
                }
                disabled={busy}
              />
              <span>Admin override (requires reason)</span>
            </label>
          </div>

          {form.override_used && (
            <>
              <label className="sched-label">Override reason</label>
              <input
                type="text"
                value={form.override_reason}
                onChange={(e) =>
                  setForm((p) => ({ ...p, override_reason: e.target.value }))
                }
                placeholder="Explain why this override is necessary..."
                disabled={busy}
              />
            </>
          )}

          {err ? <div className="sched-error">{err}</div> : null}

          <div className="sched-actions">
            <button
              className="sched-btn ghost"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              className="sched-btn primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </>
  );
}
